// Senior Level Flow — job-description markdown parsing. Ported from the shared
// template's escape-first parser, but it returns a data structure that React
// renders as real elements (nothing from the API is ever inserted as raw HTML).
// Handles headings (sections + subs), paragraphs, bullet/numbered lists
// (nested), and `**Key:**` value lines.
//
// `parseJD` + `renderInline` are intentionally generic (no CSS classes baked
// in) so other pages rendering the same markdown dialect — e.g.
// OpeningDetails.jsx's job_description — can reuse the parsing without
// inheriting this page's `.slf-*` presentation.
import React from 'react';

const NA_RE =
  /^(not specified|not disclosed|not available|n\/?a|tbd|tba|-|—)?$/i;
export const isNA = (v) => NA_RE.test(String(v || '').trim());

// [match, heading shown on the page, short label for the sub-nav]
const SECTION_NAMES = [
  [
    /^(about( the)? (role|job|position)|overview|summary|role summary)$/,
    'About the role',
    'About',
  ],
  [/responsibilit|what you.?ll do|duties/, "What you'd do", "What you'd do"],
  [
    /^(required|minimum|basic) qualifications?$|^requirements$|^qualifications$|must.have/,
    "What they're looking for",
    'Requirements',
  ],
  [
    /^(preferred|desired|additional) qualifications?$|nice.to.have|good.to.have|bonus/,
    'Nice to have',
    'Nice to have',
  ],
  [/^skills?$/, 'Skills', 'Skills'],
  [/^role profile$/, 'Role profile', 'Role profile'],
  [/^logistics$/, 'Location, pay and dates', 'Logistics'],
  [/benefits|perks/, 'Benefits', 'Benefits'],
];

export function sentenceCase(s) {
  const str = String(s)
    .trim()
    .replace(/(\w)\(s\)/g, '$1s'); // Location(s) → Locations
  return str
    .split(/(\s+)/)
    .map((w, i) => {
      if (i === 0 || /^\s+$/.test(w)) return w;
      if (/^[^a-z]*[A-Z][^a-z]*[A-Z]/.test(w)) return w; // CAMS, (CISA), AML
      return w.toLowerCase();
    })
    .join('');
}

export function nameSection(title) {
  const k = title
    .toLowerCase()
    .replace(/[^a-z' ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const hit = SECTION_NAMES.find(([re]) => re.test(k));
  if (hit) return { full: hit[1], short: hit[2] };
  const t = sentenceCase(title);
  return { full: t, short: t.length > 22 ? `${t.slice(0, 20)}…` : t };
}

// Split "A (x, y), B, C" on top-level commas only.
export function splitTop(v) {
  const out = [];
  let depth = 0;
  let buf = '';
  Array.from(String(v)).forEach((ch) => {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && !depth) {
      if (buf.trim()) out.push(buf.trim());
      buf = '';
    } else {
      buf += ch;
    }
  });
  if (buf.trim()) out.push(buf.trim());
  return out;
}

export function parseJD(md) {
  let src = String(md || '');
  // Double-encoded payloads: literal "\n" instead of real newlines.
  if (!/\n/.test(src) && /\\n/.test(src)) src = src.replace(/\\n/g, '\n');

  const sections = [];
  let sec = null;
  let para = null;
  let listCtx = null;

  const cur = () => {
    if (!sec) {
      sec = { title: null, blocks: [] };
      sections.push(sec);
    }
    return sec;
  };

  src
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((raw) => {
      const line = raw.replace(/\s+$/, '');
      if (!line.trim()) {
        para = null; // lists survive blank lines (listCtx kept)
        return;
      }
      if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
        para = null;
        listCtx = null; // horizontal rule
        return;
      }

      const heading = line.match(/^(#{1,6})\s+(.*?)\s*#*$/);
      if (heading) {
        para = null;
        listCtx = null;
        if (heading[1].length <= 2) {
          sec = { title: heading[2], blocks: [] };
          sections.push(sec);
        } else {
          cur().blocks.push({ type: 'sub', text: heading[2] });
        }
        return;
      }

      const bullet = line.match(/^(\s*)([-*+•]|\d+[.)])\s+(.*)$/);
      if (bullet) {
        para = null;
        const indent = bullet[1].replace(/\t/g, '  ').length;
        const ordered = /\d/.test(bullet[2]);
        const item = { text: bullet[3], children: null };
        const s = cur();
        if (!listCtx) {
          const list = { type: 'list', ordered, items: [] };
          const last = s.blocks[s.blocks.length - 1];
          const host =
            last && last.type === 'kv'
              ? last.items[last.items.length - 1]
              : null;
          if (host && !host.value && !host.list) host.list = list;
          else s.blocks.push(list);
          listCtx = [{ indent, list }];
        } else {
          while (
            listCtx.length > 1 &&
            indent < listCtx[listCtx.length - 1].indent
          ) {
            listCtx.pop();
          }
          const top = listCtx[listCtx.length - 1];
          const prev = top.list.items[top.list.items.length - 1];
          if (indent >= top.indent + 2 && prev) {
            prev.children = { type: 'list', ordered, items: [] };
            listCtx.push({ indent, list: prev.children });
          }
        }
        listCtx[listCtx.length - 1].list.items.push(item);
        return;
      }

      const kv =
        line.match(/^\*\*([^*]+?)\s*:\s*\*\*\s*(.*)$/) ||
        line.match(/^\*\*([^*]+?)\*\*\s*:\s*(.*)$/);
      if (kv) {
        para = null;
        listCtx = null;
        const s = cur();
        const last = s.blocks[s.blocks.length - 1];
        const item = { key: kv[1].trim(), value: kv[2].trim(), list: null };
        if (last && last.type === 'kv') last.items.push(item);
        else s.blocks.push({ type: 'kv', items: [item] });
        return;
      }

      listCtx = null;
      if (para) {
        para.text += ` ${line.trim()}`;
      } else {
        para = { type: 'p', text: line.trim() };
        cur().blocks.push(para);
      }
    });

  return sections.filter((s) => s.title || s.blocks.length);
}

// Assign a candidate-facing name + a unique anchor id to each section.
export function buildNamedSections(sections) {
  const used = {};
  return sections.map((section, i) => {
    const n = section.title
      ? nameSection(section.title)
      : { full: null, short: 'Overview' };
    const base =
      n.short
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || String(i);
    let id = `slf-jd-${base}`;
    if (used[id]) id += `-${i}`;
    used[id] = true;
    return { section, full: n.full, short: n.short, id };
  });
}

// "N sections, about M minutes to read" — the pre-open hint.
export function jdMeta(md, sections) {
  const words = String(md || '')
    .split(/\s+/)
    .filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  const titled = sections.filter((s) => s.title).length;
  return { mins, titled };
}

// ── Inline markdown → React nodes (bold, code, links). Everything is a real
// text/element node, so nothing is inserted as raw HTML. Un-styled on
// purpose — callers apply their own className/CSS-var presentation. ──
const INLINE_SRC =
  '\\*\\*([^*]+)\\*\\*|`([^`]+)`|\\[([^\\]]+)\\]\\((https?:\\/\\/[^\\s)]+)\\)';

export function renderInline(text) {
  const src = String(text ?? '');
  const out = [];
  let last = 0;
  let key = 0;
  // A fresh regex per call so lastIndex never leaks between invocations.
  const re = new RegExp(INLINE_SRC, 'g');
  let m = re.exec(src);
  while (m) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[1] != null) {
      out.push(<strong key={key}>{m[1]}</strong>);
    } else if (m[2] != null) {
      out.push(<code key={key}>{m[2]}</code>);
    } else {
      out.push(
        <a key={key} href={m[4]} target="_blank" rel="noopener noreferrer">
          {m[3]}
        </a>
      );
    }
    key += 1;
    last = re.lastIndex;
    m = re.exec(src);
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}
